// Leitura de balança Toledo (ex.: Prix 8217 de checkout, ou o indicador
// IND560 da Mettler Toledo), ligada por USB com o adaptador que cria uma porta
// COM virtual, direto do navegador, pela Web Serial API (Chrome/Edge, só em
// HTTPS ou localhost — funciona também no PDV instalado). Sem programa
// intermediário no computador. O protocolo é reconhecido sozinho pelo formato
// de cada resposta:
//
// 1) Toledo "checkout" (Prix): o PDV envia ENQ (05h) e a balança responde
//    STX (02h) + peso + ETX (03h). O peso vem em gramas com 5 ou 6 dígitos
//    ("01250" = 1,250 kg) ou já com ponto decimal ("01.250"); a balança
//    responde "IIIII" com o peso instável, "NNNNN" negativo e "SSSSS" acima da
//    capacidade. Algumas ficam configuradas pra mandar o peso sozinhas — o
//    mesmo leitor entende.
//
// 2) Saída contínua padrão Toledo (IND560 e outros indicadores Mettler
//    Toledo, porta configurada como "Contínua"): o indicador manda sozinho,
//    várias vezes por segundo, STX + 3 bytes de status (SWA, SWB, SWC) + 6
//    dígitos do peso + 6 dígitos da tara + CR (+ checksum opcional). A posição
//    da vírgula, o sinal, o movimento (instável), a faixa e a unidade vêm nos
//    bits de status — ver interpretarContinuoToledo.
//
// 3) Saída por demanda em texto ("  1.250 kg G" + CR LF): lida pelo número
//    com ponto decimal.
//
// Os bytes são abertos em 8 bits sem paridade e o 8º bit é descartado: assim
// os indicadores configurados com 7 bits + paridade par/ímpar (comum nos
// Toledo) também são lidos sem configurar nada além da velocidade.
//
// A velocidade (baud rate) tem que ser a mesma configurada na balança — ver
// Configurações do PDV → Balança.

export type TStatusBalanca = 'naoSuportado' | 'desconectada' | 'conectando' | 'conectada' | 'erro';
export type TSituacaoPeso = 'ok' | 'instavel' | 'negativo' | 'sobrecarga' | 'unidadeInvalida' | 'semResposta';

export interface ILeituraBalanca {
  // Peso em kg (3 casas). null quando não há leitura válida.
  peso: number | null;
  // Peso parado: bit de movimento desligado (saída contínua) ou o mesmo peso
  // em duas respostas seguidas (checkout/texto) — só esse vai pra quantidade.
  estavel: boolean;
  situacao: TSituacaoPeso;
}

export interface IConfigBalanca {
  // Conectar sozinho ao abrir o PDV (a porta escolhida fica lembrada pelo
  // navegador — ver conectarAutomatico).
  ativa: boolean;
  baudRate: number;
  // Porta escolhida pela última vez (pra achar a mesma entre as permitidas).
  usbVendorId?: number;
  usbProductId?: number;
  // Modo balança: a Quantidade sempre acompanha o peso estável da balança,
  // inclusive o zero (sem nada na balança, a quantidade fica 0).
  modoBalanca: boolean;
}

const CHAVE_CONFIG = 'pdv-balanca';
const STX = 0x02;
const ETX = 0x03;
const ENQ = 0x05;
const LF = 0x0a;
const CR = 0x0d;
const INTERVALO_PEDIDO_MS = 300;
const SEM_RESPOSTA_MS = 2500;

export const VELOCIDADES_BALANCA = [2400, 4800, 9600, 19200, 38400];

const CONFIG_PADRAO: IConfigBalanca = { ativa: false, baudRate: 9600, modoBalanca: false };

export const lerConfigBalanca = (): IConfigBalanca => {
  try {
    const salvo = localStorage.getItem(CHAVE_CONFIG);
    return salvo ? { ...CONFIG_PADRAO, ...JSON.parse(salvo) } : CONFIG_PADRAO;
  } catch {
    return CONFIG_PADRAO;
  }
};

const salvarConfig = (config: IConfigBalanca) => {
  try {
    localStorage.setItem(CHAVE_CONFIG, JSON.stringify(config));
  } catch {
    // Sem armazenamento: só não lembra entre aberturas do PDV.
  }
};

export const balancaSuportada = () => typeof navigator !== 'undefined' && !!navigator.serial;

// ---------- Estado e ouvintes ----------

let status: TStatusBalanca = balancaSuportada() ? 'desconectada' : 'naoSuportado';
let mensagemErro = '';
let leitura: ILeituraBalanca = { peso: null, estavel: false, situacao: 'semResposta' };
let modoBalanca = lerConfigBalanca().modoBalanca;
// Último quadro recebido, como veio (diagnóstico no cartão da balança):
// caracteres visíveis como texto, o resto como <hex>.
let ultimoQuadro = '';

type TOuvinte = () => void;
const ouvintes = new Set<TOuvinte>();
const notificar = () => ouvintes.forEach((ouvinte) => ouvinte());

export const estadoBalanca = () => ({ status, mensagemErro, leitura, modoBalanca, ultimoQuadro });
export const inscreverBalanca = (ouvinte: TOuvinte) => {
  ouvintes.add(ouvinte);
  return () => { ouvintes.delete(ouvinte); };
};

const definirStatus = (novo: TStatusBalanca, erro = '') => {
  status = novo;
  mensagemErro = erro;
  if (novo !== 'conectada') leitura = { peso: null, estavel: false, situacao: 'semResposta' };
  notificar();
};

// ---------- Interpretação da resposta ----------

// estavel só vem quando o próprio formato diz (bit de movimento da saída
// contínua); nos outros, é calculado comparando com a resposta anterior.
type TLeituraParcial = Omit<ILeituraBalanca, 'estavel'> & { estavel?: boolean };

// Texto entre STX e ETX (ou até o fim de linha) → leitura. Exportado pra teste.
export const interpretarResposta = (texto: string): TLeituraParcial => {
  const limpo = texto.trim().toUpperCase();
  if (/^I+$/.test(limpo)) return { peso: null, situacao: 'instavel' };
  if (/^N+$/.test(limpo)) return { peso: null, situacao: 'negativo' };
  if (/^S+$/.test(limpo)) return { peso: null, situacao: 'sobrecarga' };
  if (/-\s*[0-9]/.test(limpo)) return { peso: null, situacao: 'negativo' };
  // Só o PRIMEIRO número da linha (o peso) — linhas de impressão do
  // indicador podem trazer a tara depois ("20.00 kg G  0.00 kg T").
  const encontrado = limpo.match(/[0-9]+(?:[.,][0-9]+)?/);
  if (!encontrado) return { peso: null, situacao: 'semResposta' };
  const numero = encontrado[0].replace(',', '.');
  let kg: number;
  if (/^[0-9]+$/.test(limpo)) kg = parseInt(numero, 10) / 1000; // checkout: só dígitos = gramas
  else if (/\bLB\b/.test(limpo)) kg = parseFloat(numero) * 0.45359237;
  else kg = parseFloat(numero); // texto com ponto decimal ou unidade: já em kg
  if (!Number.isFinite(kg)) return { peso: null, situacao: 'semResposta' };
  return { peso: Math.round(kg * 1000) / 1000, situacao: 'ok' };
};

const ehDigito = (byte: number) => byte >= 0x30 && byte <= 0x39;
// Bytes de status da saída contínua Toledo: bit 5 sempre 1, bit 6 sempre 0.
const ehStatusToledo = (byte: number) => (byte & 0x60) === 0x20;

// Quadro da saída contínua padrão Toledo (IND560 etc.), sem o STX e o CR:
// SWA SWB SWC + 6 dígitos do peso + 6 da tara. null se não for esse formato.
// Exportado pra teste.
export const interpretarContinuoToledo = (bytes: number[]): TLeituraParcial | null => {
  if (bytes.length < 15) return null;
  const [swa, swb, swc] = bytes;
  if (!ehStatusToledo(swa) || !ehStatusToledo(swb) || !ehStatusToledo(swc)) return null;
  const digitos = bytes.slice(3, 15);
  if (!digitos.every(ehDigito)) return null;

  // SWB: bit 1 = negativo, bit 2 = fora da faixa, bit 3 = em movimento,
  // bit 4 = kg (0 = lb).
  if (swb & 0x04) return { peso: null, situacao: 'sobrecarga' };
  if (swb & 0x02) return { peso: null, situacao: 'negativo' };
  const emMovimento = (swb & 0x08) !== 0;

  // SWA bits 0-2: posição da vírgula. 0 = X00, 1 = X0, 2 = sem casas,
  // 3 = 1 casa, 4 = 2 casas ... 7 = 5 casas.
  const ponto = swa & 0x07;
  const fator = ponto === 0 ? 100 : ponto === 1 ? 10 : 1 / 10 ** (ponto - 2);
  const valor = parseInt(String.fromCharCode(...digitos.slice(0, 6)), 10) * fator;

  // SWC bits 0-2: unidade. 0 = kg/lb (pelo SWB), 1 = g, 2 = t; as outras
  // (onça, unidade personalizada...) não servem como quantidade em kg.
  const unidade = swc & 0x07;
  let kg: number;
  if (unidade === 0) kg = swb & 0x10 ? valor : valor * 0.45359237;
  else if (unidade === 1) kg = valor / 1000;
  else if (unidade === 2) kg = valor * 1000;
  else return { peso: null, situacao: 'unidadeInvalida' };

  if (!Number.isFinite(kg)) return { peso: null, situacao: 'semResposta' };
  return { peso: Math.round(kg * 1000) / 1000, situacao: 'ok', estavel: !emMovimento };
};

let ultimoPesoBruto: number | null = null;
let ultimaRespostaEm = 0;
// O indicador está mandando a saída contínua sozinho — não precisa pedir.
let recebendoContinuo = false;

const registrarQuadro = (bytes: number[]) => {
  const continuo = interpretarContinuoToledo(bytes);
  if (continuo) recebendoContinuo = true;
  const atual = continuo || interpretarResposta(String.fromCharCode(...bytes));
  ultimaRespostaEm = Date.now();
  const estavel = atual.situacao === 'ok' && atual.peso !== null
    && (atual.estavel !== undefined ? atual.estavel : atual.peso === ultimoPesoBruto);
  ultimoPesoBruto = atual.peso;
  const nova: ILeituraBalanca = { peso: atual.peso, situacao: atual.situacao, estavel };
  const quadro = (continuo ? '[contínuo] ' : '[texto] ') + bytes
    .map((byte) => (byte >= 0x20 && byte < 0x7f ? String.fromCharCode(byte) : `<${byte.toString(16).padStart(2, '0')}>`))
    .join('');
  if (nova.peso !== leitura.peso || nova.estavel !== leitura.estavel || nova.situacao !== leitura.situacao
    || quadro !== ultimoQuadro) {
    leitura = nova;
    ultimoQuadro = quadro;
    notificar();
  }
};

// ---------- Conexão ----------

let portaAtual: SerialPort | null = null;
let leitor: ReadableStreamDefaultReader<Uint8Array> | null = null;
let timerPedido: ReturnType<typeof setInterval> | null = null;
let encerrando = false;

const pararPedidos = () => {
  if (timerPedido) clearInterval(timerPedido);
  timerPedido = null;
};

const lerContinuamente = async (porta: SerialPort) => {
  let buffer: number[] = [];
  while (porta.readable && !encerrando) {
    leitor = porta.readable.getReader();
    try {
      for (;;) {
        const { value, done } = await leitor.read();
        if (done) break;
        if (!value) continue;
        for (const byteLido of Array.from(value)) {
          // Descarta o bit de paridade (indicador em 7 bits + paridade).
          const byte = byteLido & 0x7f;
          if (byte === STX) {
            buffer = [];
          } else if (byte === ETX || byte === CR || byte === LF) {
            // ETX fecha a resposta do checkout; CR fecha o quadro da saída
            // contínua e as linhas de texto. O checksum que a saída contínua
            // pode mandar depois do CR cai num buffer que o próximo STX limpa.
            if (buffer.length) registrarQuadro(buffer);
            buffer = [];
          } else if (buffer.length < 32) {
            buffer.push(byte);
          }
        }
      }
    } catch {
      // Erro de quadro/paridade (velocidade diferente da balança, ruído) não é
      // fatal: o navegador troca o porta.readable e o laço continua lendo.
      // Erro fatal (cabo removido, porta fechada) deixa readable null e sai.
      buffer = [];
    } finally {
      leitor?.releaseLock();
      leitor = null;
    }
  }
};

const iniciarPedidos = (porta: SerialPort) => {
  pararPedidos();
  ultimaRespostaEm = Date.now();
  timerPedido = setInterval(async () => {
    if (Date.now() - ultimaRespostaEm > SEM_RESPOSTA_MS && leitura.situacao !== 'semResposta') {
      leitura = { peso: null, estavel: false, situacao: 'semResposta' };
      ultimoPesoBruto = null;
      notificar();
    }
    if (recebendoContinuo || !porta.writable || porta.writable.locked) return;
    const escritor = porta.writable.getWriter();
    try {
      await escritor.write(new Uint8Array([ENQ]));
    } catch {
      // Falha pontual de escrita — a próxima tentativa vem em seguida.
    } finally {
      escritor.releaseLock();
    }
  }, INTERVALO_PEDIDO_MS);
};

const mensagemErroAbertura = (erro: unknown) => {
  const nome = erro instanceof Error ? erro.name : '';
  const detalhe = erro instanceof Error && erro.message ? ` (${nome}: ${erro.message})` : '';
  if (nome === 'NetworkError') {
    return 'Não foi possível abrir a porta da balança: ela está em uso por outro programa (terminal serial, programa da balança'
      + ` ou o PDV aberto em outra aba/janela). Feche-o e tente de novo.${detalhe}`;
  }
  return `Não foi possível abrir a porta da balança.${detalhe}`;
};

const abrirPorta = async (porta: SerialPort, baudRate: number) => {
  encerrando = false;
  definirStatus('conectando');
  const opcoes: SerialOptions = { baudRate, dataBits: 8, stopBits: 1, parity: 'none', flowControl: 'none' };
  try {
    try {
      await porta.open(opcoes);
    } catch (erro) {
      // InvalidStateError = a porta já está aberta NESTA página (ex.: sobrou
      // de uma conexão que caiu). Fecha e abre de novo com a velocidade nova.
      if (!(erro instanceof Error && erro.name === 'InvalidStateError')) throw erro;
      await porta.close();
      await porta.open(opcoes);
    }
  } catch (erro) {
    definirStatus('erro', mensagemErroAbertura(erro));
    throw erro;
  }
  portaAtual = porta;
  ultimoPesoBruto = null;
  recebendoContinuo = false;
  definirStatus('conectada');
  iniciarPedidos(porta);
  lerContinuamente(porta).finally(() => {
    if (!encerrando && portaAtual === porta) {
      pararPedidos();
      portaAtual = null;
      // Fecha de verdade — porta aberta sem leitor impediria reconectar.
      porta.close().catch(() => undefined);
      definirStatus('desconectada');
    }
  });
};

export const desconectarBalanca = async (manterAtiva = false) => {
  encerrando = true;
  pararPedidos();
  try {
    await leitor?.cancel();
  } catch {
    // já fechado
  }
  try {
    await portaAtual?.close();
  } catch {
    // já fechada
  }
  portaAtual = null;
  if (!manterAtiva) salvarConfig({ ...lerConfigBalanca(), ativa: false });
  definirStatus(balancaSuportada() ? 'desconectada' : 'naoSuportado');
};

// Botão "Conectar balança" — precisa ser chamado num clique (o navegador
// mostra a lista de portas pro usuário escolher e lembra a permissão).
export const escolherEConectarBalanca = async (baudRate: number) => {
  if (!navigator.serial) {
    definirStatus('naoSuportado');
    return;
  }
  let porta: SerialPort;
  try {
    porta = await navigator.serial.requestPort();
  } catch {
    return; // usuário cancelou a escolha
  }
  await desconectarBalanca(true);
  const info = porta.getInfo();
  salvarConfig({ ...lerConfigBalanca(), ativa: true, baudRate, usbVendorId: info.usbVendorId, usbProductId: info.usbProductId });
  await abrirPorta(porta, baudRate).catch(() => undefined);
};

// Ao abrir o PDV: se a balança estava ativa, reconecta sozinho na mesma
// porta (já permitida antes — getPorts não pede nada ao usuário).
export const conectarBalancaAutomatico = async () => {
  const config = lerConfigBalanca();
  if (!config.ativa || !navigator.serial || portaAtual) return;
  const portas = await navigator.serial.getPorts();
  const porta = portas.find((p) => {
    const info = p.getInfo();
    return info.usbVendorId === config.usbVendorId && info.usbProductId === config.usbProductId;
  }) || (portas.length === 1 ? portas[0] : undefined);
  if (porta) await abrirPorta(porta, config.baudRate).catch(() => undefined);
};

// Cabo da balança reconectado com o PDV aberto: volta a ler sozinho.
if (typeof navigator !== 'undefined' && navigator.serial) {
  navigator.serial.addEventListener('connect', () => {
    conectarBalancaAutomatico();
  });
}

// Troca a velocidade (tem que ser a mesma configurada na balança). Se já
// estiver conectada, reabre a mesma porta com a velocidade nova.
export const alterarVelocidadeBalanca = async (baudRate: number) => {
  salvarConfig({ ...lerConfigBalanca(), baudRate });
  const porta = portaAtual;
  if (porta) {
    await desconectarBalanca(true);
    await abrirPorta(porta, baudRate).catch(() => undefined);
  }
};

// Liga/desliga o modo balança (Quantidade sempre igual ao peso, inclusive 0).
export const alterarModoBalanca = (ativo: boolean) => {
  salvarConfig({ ...lerConfigBalanca(), modoBalanca: ativo });
  modoBalanca = ativo;
  notificar();
};
